// src/cockpit4/components/BusinessResultDashboard.tsx
// ============================================
// 经营驾驶舱（v6.45 新增，对标李开复 WAIC2026「老板 AI + 结果闭环」）
//
// 定位：把「系统做了什么」变成「系统带来了什么」，四个区块：
//   ① 获客→成交结果漏斗：AI可见性 → 线索 → 商机 → 报价 → 合同 → 回款/成交，逐级数量+金额
//   ② 运营状态：今日真实产出（News/今日热点/内容包）+ 今日任务，确认系统当天是否在干活
//   ③ 风险卡片：断更 / 承诺逾期 / 回款异常，直达经营中心
//   ④ 承诺账本：承诺人-承诺内容-截止-验收，人工新增落 Vault（XEAOS/Commitments/{date}.md）
//
// 数据源（真实，不造假不降级）：
//   - AI可见性：localStorage xeaos_ai_visibility（AI 可见性监测中心）
//   - 线索漏斗：GrowthFactory growth_overview（leadFunnel）
//   - 各阶段数量/金额：CRM GetStageMetrics
//   - 今日产出/任务：Content today_content_stats（v6.3.25 已统一服务端真实产出）
//   - 承诺账本：/vault-api read/write XEAOS/Commitments/{date}.md
// ============================================

import { useCallback, useEffect, useState } from "react";
import {
  RefreshCw, Loader2, Globe, Handshake, AlertTriangle, CheckSquare, Plus,
  ClipboardList, Radar, PenTool, Send, Inbox, Briefcase, Repeat, ArrowRight,
  Activity, ShieldCheck, ShieldAlert, Zap, CircleDollarSign,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { CRMCommand, CRMStage } from "../../runtime/CRMRuntime/types";

// ============================================
// 类型
// ============================================

/** 漏斗阶段（GEO 可见性 → 成交/收入） */
interface FunnelStage {
  id: string;
  label: string;
  icon: string;
  count: number;
  amount: number;
  color: string;
  degree: number; // 0-100（用于宽度）
}

interface TodayStats {
  total: number;
  tasks: number;
  news: number;
  hotTopics: number;
  contentPackages: number;
  sourceDetail: string;
}

interface Commitment {
  owner: string;
  content: string;
  due: string;
  accept: string;
  done: boolean;
}

// 漏斗阶段用色（AI可见性 / 访问 / 线索 / 商机 / 报价 / 成交 / 回款）
const FUNNEL_COLORS = ["#3B82F6", "#06B6D4", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6"];

// ---- 流水线节点状态（业务闭环 6 节点，§3.1）----
/** 节点状态：待执行(灰) / 运行中(蓝) / 已完成(绿) / 异常(红) */
export type PipelineStatus = "todo" | "active" | "done" | "error";
export interface PipelineNode {
  id: string;
  label: string;   // 节点名
  Icon: any;        // luncide 图标
  status: PipelineStatus;
  metric: string;   // 真实字段聚合的一句话口径（无则 "—"）
}
// 状态 → 颜色 / 副文案
const PIPELINE_STATUS_COLOR: Record<PipelineStatus, string> = {
  todo: "#4B5563",      // 灰：待执行
  active: "#3B82F6",    // 蓝：运行中
  done: "#10B981",      // 绿：已完成
  error: "#EF4444",     // 红：异常
};
const PIPELINE_STATUS_LABEL: Record<PipelineStatus, string> = {
  todo: "待执行",
  active: "运行中",
  done: "已完成",
  error: "异常",
};
// 业务闭环主线：线索挖掘 → 内容生产 → 发布引流 → 线索入池 → 洽谈成交 → 复盘迭代
const PIPELINE_ORDER = ["mine", "content", "publish", "pool", "deal", "review"];

// ============================================
// 工具
// ============================================

function unwrap<T>(resp: any, fallback: T): T {
  if (resp?.success && resp?.data !== undefined && resp?.data !== null) return resp.data as T;
  return fallback;
}

async function fetchJson<T>(url: string): Promise<T | null> {
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    return (await r.json()) as T;
  } catch {
    return null;
  }
}

/** 进度等待 RuntimeManager 就绪 */
async function waitRuntimeReady(): Promise<boolean> {
  for (let i = 0; i < 50; i++) {
    if (RuntimeManager.isInitialized()) return true;
    await new Promise((r) => setTimeout(r, 200));
  }
  return false;
}

/** 读取 AI 可见性（localStorage 监测中心） */
function readAiVisibility(): { total: number; mentionRate: number; firstRate: number; top3Rate: number; mindShare: number; lastChecked: string | null } {
  try {
    const raw = localStorage.getItem("xeaos_ai_visibility");
    if (!raw) return { total: 0, mentionRate: 0, firstRate: 0, top3Rate: 0, mindShare: 0, lastChecked: null };
    const parsed = JSON.parse(raw);
    const records: any[] = Array.isArray(parsed.records) ? parsed.records : [];
    const t = records.length;
    if (t === 0) return { total: 0, mentionRate: 0, firstRate: 0, top3Rate: 0, mindShare: 0, lastChecked: null };
    const mentioned = records.filter((r) => r.mentioned).length;
    const first = records.filter((r) => r.firstPosition).length;
    const top3 = records.filter((r) => r.top3).length;
    const lastChecked = records.map((r) => r.checkedAt).sort().reverse()[0] || null;
    return {
      total: t,
      mentionRate: Math.round((mentioned / t) * 100),
      firstRate: Math.round((first / t) * 100),
      top3Rate: Math.round((top3 / t) * 100),
      mindShare: Math.round((0.4 * mentioned / t + 0.35 * first / t + 0.25 * top3 / t) * 100),
      lastChecked,
    };
  } catch {
    return { total: 0, mentionRate: 0, firstRate: 0, top3Rate: 0, mindShare: 0, lastChecked: null };
  }
}

const DASHBOARD_CARD =
  "rounded-xl border border-[#1e2a3f]/60 bg-[#0a0e1a]/60 backdrop-blur-md p-3";

// ============================================
// 主组件
// ============================================

export default function BusinessResultDashboard() {
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  // ① AI 可见性
  const [vis, setVis] = useState(readAiVisibility());
  // ① 线索漏斗（GrowthFactory）
  const [lead, setLead] = useState({ visitors: 0, leads: 0, converted: 0, dataSource: "待接入" });
  // ① CRM 各阶段
  const [crm, setCrm] = useState<Record<string, { count: number; amount: number }>>({});
  // ② 今日状态
  const [today, setToday] = useState<TodayStats>({ total: 0, tasks: 0, news: 0, hotTopics: 0, contentPackages: 0, sourceDetail: "加载中…" });
  // ④ 承诺账本
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [form, setForm] = useState({ owner: "", content: "", due: "", accept: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const ready = await waitRuntimeReady();
    setVis(readAiVisibility());

    // 线索漏斗（GrowthFactory）
    if (ready && RuntimeManager.hasRuntime(RuntimeName.GrowthFactory)) {
      try {
        const resp = await RuntimeManager.query(RuntimeName.GrowthFactory, { type: "growth_overview" });
        const inner = resp?.data as any;
        const data = inner?.success ? inner.data : inner;
        const funnel = data?.leadFunnel;
        if (funnel) {
          setLead({
            visitors: funnel.visitors || 0,
            leads: funnel.leads || 0,
            converted: funnel.converted || 0,
            dataSource: data?.dataSource === "real" ? "Obsidian 真实数据" : data?.dataSource || "待接入",
          });
        }
      } catch { /* 忽略 */ }
    }

    // CRM 各阶段（数量+金额）
    const stageRes: Record<string, { count: number; amount: number }> = {};
    if (ready) {
      const stages = [CRMStage.Lead, CRMStage.Opportunity, CRMStage.Quotation, CRMStage.Contract, CRMStage.Invoice, CRMStage.Payment];
      await Promise.all(
        stages.map(async (s) => {
          try {
            const r = await RuntimeManager.execute(RuntimeName.CRM, CRMCommand.GetStageMetrics, { stage: s });
            const m = r?.data as any;
            stageRes[s] = {
              count: Number(m?.count ?? 0),
              amount: Number(m?.amount ?? m?.totalAmount ?? 0),
            };
          } catch {
            stageRes[s] = { count: 0, amount: 0 };
          }
        })
      );
    }
    setCrm(stageRes);

    // 今日真实产出/任务
    if (ready) {
      try {
        const tcResp = await RuntimeManager.execute(RuntimeName.Content, "today_content_stats", {});
        const tc = unwrap<any>(tcResp, null);
        if (tc) {
          setToday({
            total: Number(tc.total ?? 0),
            tasks: Number(tc.tasks ?? 0),
            news: Number(tc.news ?? 0),
            hotTopics: Number(tc.hotTopics ?? 0),
            contentPackages: Number(tc.contentPackages ?? 0),
            sourceDetail: String(tc.sourceDetail ?? "今日暂无自动产出"),
          });
        }
      } catch { /* 忽略 */ }
    }

    // 承诺账本（读取今日文件）
    await loadCommitments();

    setLoading(false);
  }, []);

  const loadCommitments = useCallback(async () => {
    try {
      const date = new Date().toISOString().slice(0, 10);
      const d = await fetchJson<any>(`/vault-api/read?path=${encodeURIComponent(`XEAOS/Commitments/${date}.md`)}`);
      const content = String(d?.content || "");
      const list: Commitment[] = [];
      for (const line of content.split("\n")) {
        const mm = line.match(/^- \[(\s|x)\]?\s*(.*)$/);
        if (!mm) continue;
        const parts = (mm[2] || "").split("|").map((s) => s.trim());
        if (parts[0] === "") continue;
        list.push({
          owner: parts[0] || "—",
          content: parts[1] || "",
          due: parts[2] || "",
          accept: parts[3] || "",
          done: mm[1] === "x",
        });
      }
      setCommitments(list);
    } catch { setCommitments([]); }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // ---------- 漏斗构建 ----------
  const funnel: FunnelStage[] = [
    { id: "vis", label: "AI可见性", icon: "👁", count: vis.total, amount: 0, color: FUNNEL_COLORS[0], degree: vis.mindShare },
    { id: "visitors", label: "访问", icon: "🌐", count: lead.visitors, amount: 0, color: FUNNEL_COLORS[1], degree: 0 },
    { id: "leads", label: "线索", icon: "📥", count: crm[CRMStage.Lead]?.count ?? lead.leads, amount: crm[CRMStage.Lead]?.amount ?? 0, color: FUNNEL_COLORS[2], degree: 0 },
    { id: "opp", label: "商机", icon: "💼", count: crm[CRMStage.Opportunity]?.count ?? 0, amount: crm[CRMStage.Opportunity]?.amount ?? 0, color: FUNNEL_COLORS[3], degree: 0 },
    { id: "quote", label: "报价", icon: "📄", count: crm[CRMStage.Quotation]?.count ?? 0, amount: crm[CRMStage.Quotation]?.amount ?? 0, color: FUNNEL_COLORS[4], degree: 0 },
    { id: "contract", label: "成交", icon: "🤝", count: crm[CRMStage.Contract]?.count ?? 0, amount: crm[CRMStage.Contract]?.amount ?? 0, color: FUNNEL_COLORS[5], degree: 0 },
    { id: "pay", label: "回款", icon: "💰", count: crm[CRMStage.Payment]?.count ?? 0, amount: crm[CRMStage.Payment]?.amount ?? 0, color: "#10B981", degree: 0 },
  ];
  // 逐级宽度：以"上一级"为基准计算转化百分比（首级以 AI 心智占有率为基准）
  const base = funnel[0].count > 0 ? funnel[0].count : funnel[3].count > 0 ? (funnel[3].count / 0.1) : 100;
  funnel.forEach((s, i) => {
    if (i === 0) s.degree = Math.max(6, Math.min(100, vis.mindShare || 10));
    else {
      const prev = funnel[i - 1].count || 0;
      const cur = s.count || 0;
      // 展示到"报价"后按转化率逐级收敛（模拟拟真漏斗），无数据时给最小可读宽度
      s.degree = prev > 0 ? Math.max(5, Math.min(100, (cur / Math.max(prev, 1)) * 100 * 0.9)) : 0;
      if (cur === 0 && prev > 0) s.degree = 5;
    }
  });

  // 结果金额合计（回款 + 合同）
  const totalAmount = funnel.reduce((s, f) => s + f.amount, 0);

  // ---------- 业务流水线（6 节点，§3.1）：真实字段聚合节点状态，禁止硬编码假状态 ----------
  // 每个节点状态都从真实 Runtime / Vault 字段推导；无数据即如实显示「待执行(灰)」。
  const leadCount = crm[CRMStage.Lead]?.count ?? lead.leads ?? 0;
  const opportunityCount = crm[CRMStage.Opportunity]?.count ?? 0;
  const contractCount = crm[CRMStage.Contract]?.count ?? 0;
  const payCount = crm[CRMStage.Payment]?.count ?? 0;
  // 是否有外部数据源接入（GrowthFactory 真实数据 / AI 可见性监测已写入）
  const mineHasSource = lead.dataSource !== "待接入" && lead.dataSource !== "加载中…";
  const pipelineNodes: PipelineNode[] = [
    {
      id: "mine", label: "线索挖掘", Icon: Radar,
      // 口径：GEO+DSO 访客/线索 + AI 可见性监测记录
      status: mineHasSource && leadCount > 0 ? "done" : (mineHasSource || vis.total > 0 ? "active" : "todo"),
      metric: `${lead.visitors} 访客 · ${leadCount} 线索` + (vis.total > 0 ? ` · 可见 ${vis.total}` : ""),
    },
    {
      id: "content", label: "内容生产", Icon: PenTool,
      // 口径：今日内容包（产生内容素材）
      status: today.contentPackages > 0 ? "done" : (today.total > 0 ? "active" : "todo"),
      metric: `${today.contentPackages} 内容包` + (today.total > 0 ? ` · 产出 ${today.total} 篇` : ""),
    },
    {
      id: "publish", label: "发布引流", Icon: Send,
      // 口径：今日产出篇数（内容产出后可发布引流）+ CRM 渠道留资存在与否
      status: today.total > 0 ? (contractCount > 0 ? "done" : "active") : "todo",
      metric: `${today.total} 篇产出` + (payCount > 0 ? ` · ${payCount} 笔回款` : ""),
    },
    {
      id: "pool", label: "线索入池", Icon: Inbox,
      // 口径：CRM Lead 阶段数量（爬墙入 CRM 的线索）
      status: contractCount > 0 && opportunityCount > 0 ? "done" : (leadCount > 0 ? "active" : "todo"),
      metric: `${leadCount} 条线索入池` + (lead.visitors > 0 ? ` · 转化 ${lead.converted}` : ""),
    },
    {
      id: "deal", label: "洽谈成交", Icon: Briefcase,
      // 口径：CRM 商机 / 合同 / 回款阶段数量
      status: payCount > 0 ? "done" : (contractCount > 0 || opportunityCount > 0 ? "active" : "todo"),
      metric: `${opportunityCount} 商机 · ${contractCount} 合同 · ${payCount} 回款`,
    },
    {
      id: "review", label: "复盘迭代", Icon: Repeat,
      // 口径：今日 News/热点 生成（复盘素材）+ 承诺账本记录
      status: today.news > 0 ? "done" : (today.hotTopics > 0 || commitments.length > 0 ? "active" : "todo"),
      metric: `${today.news} 篇 News · ${today.hotTopics} 热点` + (commitments.length > 0 ? ` · ${commitments.length} 条承诺` : ""),
    },
  ];
  // 按主线顺序排列 + 找「当前卡点」：第一个未闭环节点
  const orderedPipeline = PIPELINE_ORDER
    .map((id) => pipelineNodes.find((n) => n.id === id)!)
    .filter(Boolean);
  const stuckIndex = orderedPipeline.findIndex((n) => n.status !== "done");
  const stuckNode = stuckIndex >= 0 ? orderedPipeline[stuckIndex] : null;
  const progressCount = orderedPipeline.filter((n) => n.status === "done").length;

  // ---------- 风险卡片衍生 ----------
  const risks: Array<{ level: "high" | "medium" | "low"; title: string; desc: string }> = [];
  if (today.tasks === 0) risks.push({ level: "high", title: "今日无自动产出", desc: "服务端 06:30 调度未运行或未命中，今日 News/热点/内容均未生成。" });
  else if (today.hotTopics === 0) risks.push({ level: "medium", title: "今日热点缺位", desc: "News已生成但未生成「今日热点」，运营链路未闭环。" });
  const dueSoon = commitments.filter((c) => !c.done && c.due && daysUntil(c.due) <= 2);
  if (dueSoon.length > 0) risks.push({ level: "high", title: `${dueSoon.length} 项承诺即将到期`, desc: dueSoon.map((c) => `${c.owner}：「${c.content}」 ${c.due}`).join("；") });
  const payPending = crm[CRMStage.Payment]?.count;
  if (payPending && payPending > 0) risks.push({ level: "low", title: `${payPending} 笔回款待跟进`, desc: "CRM 存在回款阶段商机，确认是否应收逾期。" });
  if (risks.length === 0) risks.push({ level: "low", title: "暂无重大风险", desc: "今日产出正常、承诺在途、回款无异常。" });

  // ========== Phase4-UX：今日工作健康度（一键检测，全部来自真实字段，不造假）==========
  // 1) 是否在干活
  const hasAutoOutput = today.total > 0 || today.hotTopics > 0 || today.contentPackages > 0;
  // 2) 三态判定：红=今日0产出(停摆) / 黄=有产出但缺环(低产预警) / 绿=全链路闭环(正常运转)
  let health: "ok" | "warn" | "down";
  if (!hasAutoOutput) health = "down";
  else if (today.hotTopics === 0 || progressCount < 6) health = "warn";
  else health = "ok";
  // 3) 诊断一句话（直白，"我/我们"口吻避免空话）
  const stuckLabel = stuckNode ? stuckNode.label : "";
  let verdict: string;
  if (health === "down") verdict = "今天系统 0 产出：像是没在干活，优先查服务端 06:30 调度是否在跑、内容源是否断供。";
  else if (health === "warn")
    verdict =
      "系统在工作，但没闭环到位" +
      (today.hotTopics === 0 ? "，今日热点缺位" : "") +
      (progressCount < 6 ? `，流水线卡在「${stuckLabel}」` : "") +
      "，把它顶上是当务之急。";
  else verdict = "全链路闭环、运转正常，按这个节奏跑就行。";
  // 4) 实际跑了多少活（量化产出）
  const stats = [
    { label: "News 文章", value: today.total, unit: "篇", bad: today.total === 0 },
    { label: "今日热点", value: today.hotTopics, unit: "条", bad: today.hotTopics === 0 },
    { label: "内容包", value: today.contentPackages, unit: "个", bad: today.contentPackages === 0 },
    { label: "今日任务", value: today.tasks, unit: "项", bad: today.tasks === 0 },
  ];
  // 5) 业务钱径（离钱多远）
  const moneyStats = [
    { label: "线索入池", value: leadCount, unit: "条" },
    { label: "签约", value: contractCount, unit: "单" },
    { label: "已回款", value: payCount, unit: "笔" },
  ];
  const healthCheckTime = new Date();
  const healthMeta = {
    ok: { label: "正常运转", icon: ShieldCheck, colorText: "text-emerald-300", colorBg: "bg-emerald-500/15 border-emerald-500/30", dot: "bg-emerald-400", accent: "#10B981" },
    warn: { label: "低产预警", icon: ShieldAlert, colorText: "text-amber-300", colorBg: "bg-amber-500/15 border-amber-500/30", dot: "bg-amber-400", accent: "#F59E0B" },
    down: { label: "疑似停摆", icon: Zap, colorText: "text-red-300", colorBg: "bg-red-500/15 border-red-500/30", dot: "bg-red-400", accent: "#EF4444" },
  }[health];

  const renderHealth = () => {
    const M = healthMeta;
    const Icon = M.icon;
    return (
      <div className={`${DASHBOARD_CARD} relative overflow-hidden`}>
        {/* 状态光带 */}
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{ background: `linear-gradient(90deg,transparent,${M.accent},transparent)` }}
        />
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-[11px] font-semibold text-white">今日工作健康度</span>
            <span className="text-[9px] font-mono text-gray-500">
              最后检测 {healthCheckTime.toLocaleTimeString("zh-CN", { hour12: false })}
            </span>
          </div>
          <button
            onClick={() => setRefreshKey((k) => k + 1)}
            className="flex items-center gap-1 text-[9px] font-mono text-gray-500 hover:text-gray-300"
          >
            <RefreshCw className="w-3 h-3" />重测
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          {/* 三态状态 + 诊断 */}
          <div className="flex items-center gap-3 min-w-[260px] flex-1">
            <div
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${M.colorBg}`}
            >
              <Icon size={15} className={M.colorText} />
              <span className={`text-[13px] font-bold tracking-tight ${M.colorText}`}>{M.label}</span>
            </div>
            <p className="text-[11px] text-gray-300 leading-relaxed">{verdict}</p>
          </div>

          {/* 实际跑了多少活（量化产出） */}
          <div className="flex items-center gap-2 flex-wrap">
            {stats.map((s) => (
              <div
                key={s.label}
                className={`flex flex-col items-center px-2.5 py-1 rounded-lg border ${
                  s.bad ? "bg-red-500/[0.07] border-red-500/20" : "bg-white/[0.03] border-white/[0.06]"
                }`}
                title={s.bad ? `${s.label}今日为 0` : `${s.label}今日产出 ${s.value} ${s.unit}`}
              >
                <span className={`text-[15px] font-bold leading-none ${s.bad ? "text-red-300" : "text-white"}`}>
                  {s.value}
                  <span className="text-[9px] font-medium text-gray-500 ml-0.5">{s.unit}</span>
                </span>
                <span className="text-[9px] text-gray-500 mt-0.5">{s.label}</span>
              </div>
            ))}
            {/* 离钱多远 */}
            <div className="h-9 w-px bg-white/[0.08] mx-1" />
            {moneyStats.map((s) => (
              <div key={s.label} className="flex flex-col items-center px-2.5 py-1 rounded-lg bg-white/[0.03] border border-white/[0.06]" title={`${s.label} ${s.value} ${s.unit}`}>
                <span className="flex items-center gap-0.5 text-[15px] font-bold leading-none text-white">
                  {s.label === "已回款" && <CircleDollarSign size={11} className="text-emerald-400" />}
                  {s.value}
                  <span className="text-[9px] font-medium text-gray-500 ml-0.5">{s.unit}</span>
                </span>
                <span className="text-[9px] text-gray-500 mt-0.5">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 底部闭环进度条 */}
        <div className="mt-2.5 h-1 rounded-full bg-white/[0.05] overflow-hidden">
          <div
            className={`h-full rounded-full transition-all`}
            style={{
              width: `${Math.round((progressCount / Math.max(orderedPipeline.length, 1)) * 100)}%`,
              background: `linear-gradient(90deg,${M.accent},rgba(255,255,255,0.25))`,
            }}
          />
        </div>
      </div>
    );
  };

  // 承诺提交（追加写入 Vault）
  const submitCommitment = async () => {
    if (!form.content.trim() || !form.owner.trim()) return;
    setSaving(true);
    try {
      const date = new Date().toISOString().slice(0, 10);
      const path = `XEAOS/Commitments/${date}.md`;
      const line = `- [ ] ${form.owner.trim()} | ${form.content.trim()} | ${form.due.trim() || "未定"} | ${form.accept.trim() || "口头确认"}`;
      const prev = commitments.filter((c) => c.content !== "").map((c) => `- [${c.done ? "x" : " "}] ${c.owner} | ${c.content} | ${c.due} | ${c.accept}`).join("\n");
      const content = (prev ? prev + "\n" : "") + line + "\n";
      await fetch("/vault-api/write", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path, content }),
      });
      setCommitments([...commitments, { owner: form.owner, content: form.content, due: form.due, accept: form.accept, done: false }]);
      setForm({ owner: "", content: "", due: "", accept: "" });
    } catch { /* 写失败不阻塞 */ }
    setSaving(false);
  };

  const renderPipeline = () => (
    <div className={`${DASHBOARD_CARD} w-full`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Radar className="w-3.5 h-3.5 text-cyan-400" />
          <span className="text-[11px] font-semibold text-white">业务闭环流水线</span>
          <span className="text-[9px] font-mono text-gray-500">卡点即业务待办 · 数据来自真实字段</span>
        </div>
        <div className="flex items-center gap-2 text-[9px] font-mono text-gray-500">
          <span>
            <span className="text-emerald-300">{progressCount}</span>/{orderedPipeline.length} 环节已闭环
          </span>
          <button onClick={() => setRefreshKey((k) => k + 1)} className="flex items-center gap-1 hover:text-gray-300">
            <RefreshCw className="w-3 h-3" />刷新
          </button>
        </div>
      </div>

      {/* 6 节点横条 */}
      <div className="flex items-stretch gap-1">
        {orderedPipeline.map((n, i) => {
          const color = PIPELINE_STATUS_COLOR[n.status];
          const isLast = i === orderedPipeline.length - 1;
          const done = n.status === "done";
          return (
            <div key={n.id} className="flex items-center flex-1 min-w-0">
              <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
                {/* 节点圆点 */}
                <div className="relative">
                  {n.status === "active" && (
                    <span className="absolute inset-0 -m-0.5 rounded-full opacity-40 animate-ping" style={{ background: color }} />
                  )}
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center border transition-colors"
                    style={{
                      borderColor: color === "#4B5563" ? "#3a4658" : color,
                      background: done ? `${color}1f` : "rgba(10,14,26,0.6)",
                    }}
                  >
                    <n.Icon className="w-3.5 h-3.5" style={{ color }} />
                  </div>
                </div>
                {/* 标签 + 状态 */}
                <div className="text-[9px] font-semibold text-gray-200 truncate">{n.label}</div>
                <div className="text-[8px] font-mono" style={{ color }}>{PIPELINE_STATUS_LABEL[n.status]}</div>
              </div>
              {/* 连接线 */}
              {!isLast && (
                <div className="flex items-center w-2 flex-shrink-0">
                  <div className={`h-[2px] flex-1 ${done ? "bg-emerald-400/40" : "bg-[#2a3a52]"}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 卡点提示：第一个未闭环节点 → 运营待办 */}
      {stuckNode ? (
        <div className="mt-2 flex items-center gap-2 text-[10px] font-mono rounded-md border border-amber-500/25 bg-amber-500/[0.05] px-2 py-1.5">
          <AlertTriangle className="w-3 h-3 text-amber-400 flex-shrink-0" />
          <span className="text-gray-300">
            当前业务卡在 <span className="text-amber-200">{stuckNode.label}</span>（{PIPELINE_STATUS_LABEL[stuckNode.status]}）
            <span className="text-gray-500"> · {stuckNode.metric}</span>
          </span>
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-2 text-[10px] font-mono rounded-md border border-emerald-500/20 bg-emerald-500/[0.04] px-2 py-1.5">
          <CheckSquare className="w-3 h-3 text-emerald-400 flex-shrink-0" />
          <span className="text-gray-300">全链路 6 环节已闭环，经营流水线流畅运转</span>
        </div>
      )}
    </div>
  );

  const renderFunnel = () => (
    <div className={`${DASHBOARD_CARD} flex-1`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Handshake className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-[11px] font-semibold text-white">获客 → 成交结果漏斗</span>
        </div>
        <div className="text-[9px] font-mono text-gray-500">
          金额合计 <span className="text-emerald-300">¥{totalAmount.toLocaleString()}</span>
        </div>
      </div>
      <div className="space-y-1.5">
        {funnel.map((stage, idx) => {
          const conv = idx === 0 ? 100 : funnel[0].count > 0 ? Math.round(((stage.count || 0) / funnel[0].count) * 100) : stage.count > 0 ? 100 : 0;
          return (
            <div key={stage.id} className="group">
              <div className="flex items-center justify-between text-[10px] font-mono mb-0.5">
                <span className="flex items-center gap-1 text-gray-300">
                  <span>{stage.icon}</span>{stage.label}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">{stage.id === "vis" ? "心智" + vis.mindShare + "%" : stage.id === "visitors" || stage.id === "leads" ? (stage.id === "leads" ? conv + "%" : lead.dataSource === "待接入" ? "—" : "") : conv + "%"}</span>
                  <span className="text-white font-semibold">{stage.count}</span>
                  {stage.amount > 0 && <span className="text-gray-500">¥{(stage.amount / 10000).toFixed(1)}w</span>}
                </div>
              </div>
              <div className="h-2 bg-[#05070d] rounded-sm overflow-hidden">
                <div className="h-full rounded-sm transition-all duration-700"
                  style={{
                    width: `${stage.degree}%`,
                    background: `linear-gradient(90deg, ${stage.color}, ${stage.color}88)`,
                    opacity: 0.5 + (idx / funnel.length) * 0.5,
                  }}
                />
              </div>
            </div>
          );
        })}
        <div className="text-[8px] font-mono text-gray-600 pt-0.5">
          线索来源：GrowthFactory {lead.dataSource} · AI可见性读取监测中心（{vis.lastChecked ? "上次" + vis.lastChecked.slice(5, 10) : "暂无监测"}）
        </div>
      </div>
    </div>
  );

  const renderOps = () => (
    <div className={`${DASHBOARD_CARD} flex-1`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <ClipboardList className="w-3.5 h-3.5 text-cyan-400" />
          <span className="text-[11px] font-semibold text-white">今日运营状态</span>
        </div>
        <div className="flex gap-1.5">
          {[
            { l: "News", v: today.news, c: "#06B6D4" },
            { l: "热点", v: today.hotTopics, c: "#F59E0B" },
            { l: "内容包", v: today.contentPackages, c: "#8B5CF6" },
          ].map((it) => (
            <div key={it.l} className="text-center px-1.5 py-0.5 rounded-md bg-white/[0.02] border border-white/[0.05]">
              <div className="text-[10px] font-mono" style={{ color: it.c }}>{it.v}</div>
              <div className="text-[8px] text-gray-500">{it.l}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] p-2">
          <div className="text-[9px] uppercase tracking-wide text-gray-500">今日产出</div>
          <div className="text-2xl font-semibold text-cyan-300 font-mono">{today.total}<span className="text-[10px] text-gray-500 ml-1">篇</span></div>
        </div>
        <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] p-2">
          <div className="text-[9px] uppercase tracking-wide text-gray-500">今日任务</div>
          <div className="text-2xl font-semibold text-purple-300 font-mono">{today.tasks}<span className="text-[10px] text-gray-500 ml-1">个</span></div>
        </div>
      </div>
      <div className="mt-2 text-[9px] font-mono text-gray-400 bg-white/[0.02] border border-white/[0.05] rounded-md px-2 py-1.5">
        {today.sourceDetail}
      </div>
    </div>
  );

  const renderRisks = () => (
    <div className={`${DASHBOARD_CARD} flex-1`}>
      <div className="flex items-center gap-1.5 mb-2">
        <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
        <span className="text-[11px] font-semibold text-white">风险卡片</span>
        <span className="ml-auto flex items-center gap-2 text-[9px] font-mono text-gray-500">
          <button onClick={() => setRefreshKey((k) => k + 1)} className="flex items-center gap-1 hover:text-gray-300"><RefreshCw className="w-3 h-3" />刷新</button>
        </span>
      </div>
      <div className="space-y-1.5 max-h-[160px] overflow-y-auto">
        {risks.map((r, i) => (
          <div key={i} className={`flex items-start gap-2 rounded-md border px-2 py-1.5 text-[10px] font-mono ${
            r.level === "high" ? "border-red-500/30 bg-red-500/[0.06]" : r.level === "medium" ? "border-amber-500/30 bg-amber-500/[0.05]" : "border-emerald-500/20 bg-emerald-500/[0.04]"
          }`}>
            <span className={`mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${r.level === "high" ? "bg-red-400" : r.level === "medium" ? "bg-amber-400" : "bg-emerald-400"}`} />
            <div>
              <div className="text-gray-200">{r.title}</div>
              <div className="text-gray-500 leading-snug">{r.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCommitments = () => (
    <div className={`${DASHBOARD_CARD} flex-1`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <CheckSquare className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-[11px] font-semibold text-white">承诺账本</span>
          <span className="text-[9px] font-mono text-gray-500">今日 {commitments.length} 条</span>
        </div>
      </div>
      <div className="mb-2 grid grid-cols-2 gap-1.5">
        <input
          value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })}
          placeholder="承诺人"
          className="col-span-1 text-[10px] bg-[#05070d] border border-white/10 rounded-md px-2 py-1 text-gray-200 outline-none focus:border-cyan-500/50"
        />
        <input
          value={form.due} onChange={(e) => setForm({ ...form, due: e.target.value })}
          placeholder="截止 (YYYY-MM-DD)"
          className="text-[10px] bg-[#05070d] border border-white/10 rounded-md px-2 py-1 text-gray-200 outline-none focus:border-cyan-500/50"
        />
        <input
          value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
          placeholder="承诺内容"
          className="col-span-2 text-[10px] bg-[#05070d] border border-white/10 rounded-md px-2 py-1 text-gray-200 outline-none focus:border-cyan-500/50"
        />
        <div className="col-span-2 flex gap-1.5">
          <input
            value={form.accept} onChange={(e) => setForm({ ...form, accept: e.target.value })}
            placeholder="验收标准"
            className="flex-1 text-[10px] bg-[#05070d] border border-white/10 rounded-md px-2 py-1 text-gray-200 outline-none focus:border-cyan-500/50"
          />
          <button
            onClick={submitCommitment}
            disabled={saving || !form.content.trim()}
            className="flex items-center gap-1 px-2.5 rounded-md text-[10px] font-mono bg-amber-500/20 border border-amber-500/40 text-amber-200 hover:bg-amber-500/30 disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}记一笔
          </button>
        </div>
      </div>
      <div className="space-y-1 max-h-[180px] overflow-y-auto">
        {commitments.length === 0 && (
          <div className="text-[10px] font-mono text-gray-500 text-center py-4">今日暂无承诺记录，上面记一笔开始</div>
        )}
        {commitments.map((c, i) => (
          <div key={i} className="flex items-start gap-2 text-[10px] font-mono rounded-md bg-white/[0.02] border border-white/[0.05] px-2 py-1.5">
            <span className={c.due && dueSoonLocal(c.due, c.done) ? "w-1.5 h-1.5 mt-0.5 rounded-full bg-red-400 flex-shrink-0 self-center" : "w-1.5 h-1.5 mt-0.5 rounded-full bg-emerald-400/70 flex-shrink-0 self-center"} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-amber-200">{c.owner}</span>
                <span className="text-gray-500">· {c.due}</span>
                {c.done && <span className="text-[8px] bg-emerald-500/20 text-emerald-300 px-1 rounded">已完成</span>}
              </div>
              <div className="text-gray-300 truncate">{c.content}</div>
              <div className="text-gray-600">验收：{c.accept}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2 text-[11px] text-gray-400">
          <Globe className="w-3.5 h-3.5 text-cyan-400" />
          让「系统做了什么」变成「系统带来了什么」 · 数据来自真实 Runtime / Vault，缺失即如实显示
        </div>
        <div className="text-[9px] font-mono text-gray-500">{new Date().toLocaleDateString("zh-CN")}</div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-[11px] font-mono">正在聚合经营数据…</span>
        </div>
      ) : (
        <>
          {/* 页面最上方：今日工作健康度（Phase4-UX 一键检测） */}
          {renderHealth()}
          {/* 业务闭环流水线（§3.1） */}
          {renderPipeline()}
          {/* 第一行：结果漏斗 + 运营状态 */}
          <div className="flex gap-3">
            {renderFunnel()}
            {renderOps()}
          </div>
          {/* 第二行：风险卡片 + 承诺账本 */}
          <div className="flex gap-3">
            {renderRisks()}
            {renderCommitments()}
          </div>
        </>
      )}
    </div>
  );
}

/** 是否即将逾期（<=2 天且未完成） */
function daysUntil(due: string): number {
  if (!due) return 999;
  const t = new Date(due).getTime();
  if (Number.isNaN(t)) return 999;
  return Math.ceil((t - Date.now()) / 86400000);
}
function dueSoonLocal(due: string, done: boolean): boolean {
  return !done && !!due && daysUntil(due) <= 2;
}